// File: frontend/src/components/admin/AdminUsers.tsx

import React, { useState, useEffect, useCallback } from "react";
import { Pagination } from "../../utils/Pagination";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { 
  FaSearch, 
  FaFilter, 
  FaTimes, 
  FaEye,
  FaEdit,
  FaUserEdit,
  FaPauseCircle,
  FaPlayCircle,
  FaBan,
  FaUndo,
  FaTrash,
  FaCheck,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUserPlus,
  FaKey,
  FaToggleOn
} from "react-icons/fa";
import { AdminUserDto, AdminUserFilters, PageResponse } from "../../types/admin.types";
import { UserStatusEnum } from "../../models/enums/UserStatusEnum";
import { UserRoleEnum } from "../../models/enums/UserRoleEnum";
import { 
  getAdminUsers, 
  createUser,
  changeUserPassword,
  changeUserRole,
  changeUserStatus,
  changeUserUsername,
  changeUserEmail,
  setUserVerified,
  suspendUser, 
  unsuspendUser, 
  banUser, 
  unbanUser, 
  deleteUser,
  bulkSuspendUsers,
  bulkBanUsers,
  bulkChangeRole,
  bulkDeleteUsers
} from "../../services/adminUserService";
import { generateProfileUrl } from "../../utils/slugGenerator";
import { useSiteSettings } from "../../context/SiteSettingsContext";
// The signed-in account: needed to recognise the admin's OWN row, which the
// backend refuses role, status, suspend, ban and delete operations on
import { useAuth } from "../../context/AuthContext";
import { formatAdminDate, formatRefreshTime, getStatusColor, getRoleColor } from "../../utils/adminUtils";
import { getSuspensionRemainingText } from "../../utils/userStatusUtils";
import { parseApiError } from "../../utils/parseApiError";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import {
  SUSPENSION_DEFAULT_DAYS,
  SUSPENSION_MAX_DAYS,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  ADMIN_REASON_MAX_LENGTH,
  ADMIN_EMAIL_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter, AdminRefreshButton } from "./common";
// Number input with custom, always-visible up/down arrows (native spinner is unreliable)
import { InputClearButton } from "../common/InputClearButton";
import NumberStepper from "./NumberStepper";
import "./AdminUsers.css";

interface AdminUsersProps {
  isAdmin: boolean;
}

// Username validation
// SettingConstants.USERNAME_PATTERN and RegisterModal.tsx. Keep in sync.
const USERNAME_REGEX = /^[a-zçğıöşü0-9_]+( [a-zçğıöşü0-9_]+)*$/;
const USERNAME_MIN_LENGTH = 3;
// Fallback max length — the effective value comes from the admin-configurable
// "user.username.max_length" system setting via useSiteSettings (hard ceiling 50)
const USERNAME_MAX_LENGTH_FALLBACK = 50;

/**
 * Normalize a username input the same way the backend does:
 * trim, collapse consecutive spaces, Turkish-aware lowercase.
 */
const normalizeUsername = (raw: string): string =>
  raw.trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");

/**
 * Validate a normalized username. Returns an i18n error key or null when valid.
 * Reuses the registration error keys so TR/EN texts stay in one place.
 * maxLength is the admin-configurable value from useSiteSettings.
 */
const validateUsernameInput = (normalized: string, maxLength: number): string | null => {
  if (!normalized) return "register_error_username_required";
  if (normalized.length < USERNAME_MIN_LENGTH) return "register_error_username_min_length";
  if (normalized.length > maxLength) return "register_error_username_max_length";
  if (!USERNAME_REGEX.test(normalized)) return "register_error_username_invalid_chars";
  return null;
};

/**
 * Roles a MODERATOR is never allowed to act on.
 *
 * The backend enforces exactly this set (ADMIN_ONLY_TARGET_ROLES in
 * AdminUserService) and it checks the target's ROLE, not its status — so a
 * suspended moderator stays protected from other moderators too. The panel
 * mirrors the rule instead of only reacting to the 403 afterwards: a moderator
 * used to be shown an open role dropdown, status dropdown and password field on
 * an ADMIN account, all of which could only ever come back rejected.
 */
const STAFF_ROLES: readonly UserRoleEnum[] = [UserRoleEnum.ADMIN, UserRoleEnum.MODERATOR];

/**
 * Returns one or two sub-text lines to display below the status badge
 * for statuses where a relevant date exists.
 *
 * - FROZEN_BY_USER    → freeze request date
 * - DELETED_BY_USER   → deletion request date  +  scheduled hard-delete date (if set)
 * - DELETED_BY_ADMIN  → statusChangedAt (= the moment admin deleted the account)
 * - BANNED            → statusChangedAt (= the moment the ban was applied)
 * - SUSPENDED         → handled separately via suspensionEndDate / suspension-info span
 *
 * Returns null for ACTIVE / INACTIVE (no relevant lifecycle date to surface).
 */
const getStatusDateInfo = (
  user: AdminUserDto,
  t: (key: string) => string
): { primary: string; secondary?: string } | null => {
  switch (user.status) {
    case UserStatusEnum.FROZEN_BY_USER:
      return user.freezeRequestedAt
        ? { primary: `${t("admin_tooltip_frozen")} ${formatAdminDate(user.freezeRequestedAt)}` }
        : null;

    case UserStatusEnum.DELETED_BY_USER:
      // Already carried out — the request dates are cleared at that point, so the
      // only meaningful date left is when the account was actually closed.
      if (user.closedAt) {
        return { primary: `${t("admin_tooltip_deletion_date")} ${formatAdminDate(user.closedAt)}` };
      }
      if (!user.deletionRequestedAt) return null;
      return {
        primary: `${t("admin_tooltip_deletion_request")} ${formatAdminDate(user.deletionRequestedAt)}`,
        secondary: user.scheduledDeletionAt
          ? `${t("admin_tooltip_deletion_date")} ${formatAdminDate(user.scheduledDeletionAt)}`
          : undefined,
      };

    case UserStatusEnum.DELETED_BY_ADMIN:
      return user.statusChangedAt
        ? { primary: `${t("admin_tooltip_deletion_date")} ${formatAdminDate(user.statusChangedAt)}` }
        : null;

    case UserStatusEnum.BANNED:
      return user.statusChangedAt
        ? { primary: `${t("admin_tooltip_banned")} ${formatAdminDate(user.statusChangedAt)}` }
        : null;

    default:
      return null;
  }
};

/**
 * Map a full status i18n key to its abbreviated counterpart used inside the
 * status column badge, e.g. "status_deleted_by_admin" → "status_short_deleted_by_admin".
 *
 * The status column is deliberately narrow (see .status-col in AdminUsers.css),
 * so long lifecycle labels such as "yönetici tarafından silinmiş" would stretch
 * the whole table. The badge therefore shows the short form and the full wording
 * is always repeated as the first line of the hover tooltip, so no information
 * is lost. Only the admin table abbreviates — every other screen keeps using the
 * plain status_* keys.
 */
const getStatusShortLabelKey = (statusLabelKey: string): string =>
  statusLabelKey.replace(/^status_/, "status_short_");

export const AdminUsers: React.FC<AdminUsersProps> = ({ isAdmin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Admin-configurable username max length from public site settings
  const { usernameMaxLength: usernameMaxLengthSetting } = useSiteSettings();
  const usernameMaxLength = usernameMaxLengthSetting || USERNAME_MAX_LENGTH_FALLBACK;
  // Id of the account currently signed in — used to recognise its own row
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;

  /**
   * True when the signed-in user may act on this account at all.
   * ADMIN may act on anyone; MODERATOR may not touch ADMIN or MODERATOR accounts.
   */
  const canManageUser = (user: AdminUserDto): boolean =>
    isAdmin || !STAFF_ROLES.includes(user.role as UserRoleEnum);

  /**
   * True when this row IS the signed-in account.
   *
   * The backend refuses role change, status change, suspension, ban and deletion
   * on the caller's own account (CANNOT_CHANGE_OWN_ROLE / CANNOT_MODIFY_SELF).
   * Mirrored here so those buttons come up greyed out instead of opening a modal
   * whose only possible outcome is an error toast. Password, e-mail, username and
   * the verified badge stay editable — the backend allows all four on oneself.
   */
  const isSelf = (user: AdminUserDto): boolean =>
    currentUserId !== null && user.id === currentUserId;

  /**
   * True when this row's role is ADMIN.
   *
   * An ADMIN account is protected from status change, suspension, ban and
   * deletion for everyone, the acting admin included — it has to be demoted
   * first (CANNOT_TARGET_ADMIN). Role change and password change stay open, which
   * is what makes that demotion possible in the first place.
   */
  const isAdminRow = (user: AdminUserDto): boolean =>
    (user.role as UserRoleEnum) === UserRoleEnum.ADMIN;

  /**
   * Whether this row may take part in a bulk operation.
   *
   * Every bulk endpoint refuses the caller's own id, so leaving the own row
   * selectable only produced a partial result with one guaranteed failure in it.
   * Other admin rows stay selectable: bulk suspend/ban/delete skip them, but bulk
   * role change works on them, and that is the one bulk action that makes sense
   * on an admin.
   */
  const isSelectable = (user: AdminUserDto): boolean =>
    canManageUser(user) && !isSelf(user);

  /**
   * Whether the signed-in account may set the verified badge on this row.
   *
   * This is the one exception to the protected-target rule. The badge grants no
   * permission and changes no lifecycle state — it is a profile marker — so a
   * role may set it on its own level: an ADMIN can verify another ADMIN (and
   * themselves), and a MODERATOR can verify another MODERATOR (and themselves)
   * for the same reason. Reaching upwards is still refused, so a moderator
   * cannot touch an ADMIN account's badge.
   */
  const canSetVerified = (user: AdminUserDto): boolean =>
    isAdmin || !isAdminRow(user);

  // Data state
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Manual refresh state — kept separate from `loading` so a refresh spins the
  // icon in place instead of replacing the table with the loading placeholder
  // (same behaviour as AdminRankBoard / AdminCustomTopics).
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  // Floating status tooltip.
  // .users-table-container needs overflow-x:auto for the sticky actions column,
  // and a scroll container clips absolutely positioned children on every side —
  // that is what cut the tooltip off on the top rows. The tooltip is therefore
  // rendered once at the component root with position:fixed, and its coordinates
  // come from the hovered badge's bounding rect.
  const [statusTooltip, setStatusTooltip] = useState<{
    text: string;
    x: number;
    y: number;
    below: boolean;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filter state
  const [filters, setFilters] = useState<AdminUserFilters>({
    status: "",
    role: "",
    search: "",
    sortBy: "registrationDate",
    sortDir: "desc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // Selection state for bulk operations
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<string>("");
  const [targetUser, setTargetUser] = useState<AdminUserDto | null>(null);
  const [suspendDays, setSuspendDays] = useState(7);
  // Open-ended suspension. Sends days = 0, which the backend stores as a null
  // suspension_end_date — SuspensionExpiryScheduler skips those on purpose, so
  // the account stays suspended until an admin lifts it by hand.
  const [suspendIndefinite, setSuspendIndefinite] = useState(false);
  const [actionReason, setActionReason] = useState("");
  const [newRole, setNewRole] = useState<UserRoleEnum>(UserRoleEnum.AUTHOR);
  // Separate state for the lifecycle status change modal
  const [newStatus, setNewStatus] = useState<UserStatusEnum>(UserStatusEnum.SUSPENDED);

  // Create user form state
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<UserRoleEnum>(UserRoleEnum.ROOKIE);

  // Change password form state
  const [changePasswordValue, setChangePasswordValue] = useState("");
  const [changePasswordConfirm, setChangePasswordConfirm] = useState("");

  // Edit user modal — username / email / verified overrides
  const [editNewUsername, setEditNewUsername] = useState("");
  const [editNewEmail, setEditNewEmail] = useState("");
  const [editVerified, setEditVerified] = useState(false);

  // Delete modal is split into two explicit sections chosen by a radio:
  //   "soft" → account is frozen (DELETED_BY_ADMIN), row + data kept
  //   "hard" → user row and all data physically removed from the DB (irreversible)
  const [deleteMode, setDeleteMode] = useState<"soft" | "hard">("soft");
  // Within SOFT mode: optionally also delete the user's entries (soft entry delete)
  const [deleteEntries, setDeleteEntries] = useState(false);
  // hardDelete = entries removed physically (only meaningful inside SOFT + deleteEntries)
  const [hardDelete, setHardDelete] = useState(false);
  // Typed confirmation required only in HARD mode
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState("");
  // Optional admin note for the deletion — stored in the audit log
  const [deleteReason, setDeleteReason] = useState("");

  // Toast state
  const [successToast, setSuccessToast] = useAutoDismiss();
  const [errorToast, setErrorToast] = useAutoDismiss();

  // Safety net only. Every button that opens this modal is disabled on a protected
  // target, so this should never be true — it keeps the fields and the confirm
  // button inert if some future path opens the modal without going through them.
  const targetLocked = !!targetUser && !canManageUser(targetUser);

  // The two backend rules that apply to admins as well, resolved for the modal's
  // current target. The row buttons already refuse to open a modal that could
  // only be rejected; these keep the individual FIELDS honest inside the combined
  // edit modal, which stays open on one's own account precisely because username,
  // e-mail, password and the verified badge are all still editable there.
  const targetIsSelf = !!targetUser && isSelf(targetUser);
  const targetIsAdminRow = !!targetUser && isAdminRow(targetUser);
  // Nobody changes their own role — another admin has to do it
  const roleFieldLocked = targetLocked || targetIsSelf;
  // Status never moves on one's own account, nor on any admin account. Restoring
  // an already suspended/banned admin is not done here but from the dedicated
  // unsuspend / unban buttons, which stay enabled.
  const statusFieldLocked = targetLocked || targetIsSelf || targetIsAdminRow;
  // The verified badge follows its own rule (see canSetVerified): it stays open
  // on a peer-level account, which is why the edit modal opens at all for a
  // moderator looking at another moderator.
  const verifiedFieldLocked = !targetUser || !canSetVerified(targetUser);

  // Fetch users
  // isRefresh = true when triggered by the toolbar refresh button: the table
  // stays on screen and only the button icon spins.
  const fetchUsers = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response: PageResponse<AdminUserDto> = await getAdminUsers(currentPage, pageSize, filters);
      setUsers(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
      setLastRefreshedAt(new Date());
    } catch (err: any) {
      setError(err.message || t("admin_users_fetch_error"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, pageSize, filters, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    setSelectedUsers(new Set());
    setSelectAll(false);
  }, [currentPage, filters]);

  const showToast = (message: string, type: "success" | "error") => {
    if (type === "success") setSuccessToast(message);
    else setErrorToast(message);
  };

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput }));
    setCurrentPage(0);
  };

  // Clear-X next to the search box: empties the field AND drops the applied
  // search, so one click really does undo the search instead of leaving the
  // list filtered by a term that is no longer visible anywhere.
  const handleClearSearch = () => {
    setSearchInput("");
    setFilters((prev) => ({ ...prev, search: "" }));
    setCurrentPage(0);
  };

  const handleFilterChange = (key: keyof AdminUserFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc" ? <FaSortUp className="sort-icon active" /> : <FaSortDown className="sort-icon active" />;
  };

  const clearFilters = () => {
    setFilters({ status: "", role: "", search: "", sortBy: "registrationDate", sortDir: "desc" });
    setSearchInput("");
    setCurrentPage(0);
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) { newSet.delete(userId); } else { newSet.add(userId); }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // Only rows this account may actually act on — otherwise "select all" would
    // queue ADMIN/MODERATOR accounts into a bulk call that the backend refuses
    // one row at a time, and the moderator would get a half-failed operation.
    if (selectAll) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.filter(isSelectable).map((u) => u.id)));
    }
    setSelectAll(!selectAll);
  };

  const openActionModal = (action: string, user?: AdminUserDto) => {
    setActionType(action);
    setTargetUser(user || null);
    setSuspendDays(SUSPENSION_DEFAULT_DAYS);
    setSuspendIndefinite(false);
    setActionReason("");
    setNewRole(user?.role ?? UserRoleEnum.AUTHOR);
    // Pre-fill status from the user's actual current status so the editUser modal is pre-populated
    setNewStatus(user?.status ?? UserStatusEnum.ACTIVE);
    setEditNewUsername(user?.username ?? "");
    setEditNewEmail(user?.email ?? "");
    setEditVerified(user?.isVerified ?? false);
    setNewUsername("");
    setNewEmail("");
    setNewPassword("");
    setNewUserRole(UserRoleEnum.ROOKIE);
    setChangePasswordValue("");
    setChangePasswordConfirm("");
    // Reset delete options each time the modal opens
    setDeleteMode("soft");
    setDeleteEntries(false);
    setHardDelete(false);
    setHardDeleteConfirmText("");
    setDeleteReason("");
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setActionType("");
    setTargetUser(null);
    setDeleteMode("soft");
    setDeleteEntries(false);
    setHardDelete(false);
    setHardDeleteConfirmText("");
    setDeleteReason("");
  };

  /**
   * Validate a password against the shared min/max rules.
   * Returns the i18n key of the failure, or null when the password is fine.
   * Used by create-user, change-password and the edit-user modal so all three
   * enforce exactly the same rule as registration.
   */
  const validatePasswordInput = (password: string): string | null => {
    // An empty password falls through to the min-length branch on purpose —
    // "must be at least N characters" is the useful message here, and the
    // create-user flow already rejects empty fields before reaching this point
    if (password.length < PASSWORD_MIN_LENGTH) return "admin_password_min_length";
    if (password.length > PASSWORD_MAX_LENGTH) return "admin_password_max_length";
    return null;
  };

  const handleCreateUser = async () => {
    if (!newUsername || !newEmail || !newPassword) {
      showToast(t("admin_create_user_fill_all"), "error");
      return;
    }
    // Normalize + validate username before hitting the API (same rules as registration)
    const normalizedUsername = normalizeUsername(newUsername);
    const usernameErrorKey = validateUsernameInput(normalizedUsername, usernameMaxLength);
    if (usernameErrorKey) {
      showToast(t(usernameErrorKey, { max: usernameMaxLength }), "error");
      return;
    }
    // Same password rules as public registration — the backend enforces them
    // too, this only avoids a pointless round-trip
    const passwordErrorKey = validatePasswordInput(newPassword);
    if (passwordErrorKey) {
      showToast(t(passwordErrorKey, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }), "error");
      return;
    }
    await createUser(normalizedUsername, newEmail, newPassword, newUserRole);
    showToast(t("admin_user_created"), "success");
    closeActionModal();
    fetchUsers();
  };

  const handleChangePassword = async () => {
    const passwordErrorKey = validatePasswordInput(changePasswordValue);
    if (passwordErrorKey) {
      showToast(t(passwordErrorKey, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }), "error");
      return;
    }
    if (changePasswordValue !== changePasswordConfirm) {
      showToast(t("admin_password_mismatch"), "error");
      return;
    }
    await changeUserPassword(targetUser!.id, changePasswordValue);
    showToast(t("admin_password_changed"), "success");
    closeActionModal();
    fetchUsers();
  };

  const handleEditUser = async () => {
    if (changePasswordValue) {
      const passwordErrorKey = validatePasswordInput(changePasswordValue);
      if (passwordErrorKey) {
        showToast(t(passwordErrorKey, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }), "error");
        return;
      }
      if (changePasswordValue !== changePasswordConfirm) {
        showToast(t("admin_password_mismatch"), "error");
        return;
      }
    }

    const calls: Promise<unknown>[] = [];

    // Normalize the edited username so " Yeni Ad " and "yeni ad" compare equal
    const normalizedEditUsername = normalizeUsername(editNewUsername);
    if (normalizedEditUsername && normalizedEditUsername !== targetUser!.username) {
      // Validate against the same rules as registration before calling the API
      const usernameErrorKey = validateUsernameInput(normalizedEditUsername, usernameMaxLength);
      if (usernameErrorKey) {
        showToast(t(usernameErrorKey, { max: usernameMaxLength }), "error");
        return;
      }
      calls.push(changeUserUsername(targetUser!.id, normalizedEditUsername));
    }

    if (editNewEmail.trim() && editNewEmail.trim() !== targetUser!.email)
      calls.push(changeUserEmail(targetUser!.id, editNewEmail.trim()));

    if (editVerified !== targetUser!.isVerified)
      calls.push(setUserVerified(targetUser!.id, editVerified));

    if (newRole !== targetUser!.role)
      calls.push(changeUserRole(targetUser!.id, newRole));

    if (newStatus !== targetUser!.status) {
      if (newStatus === UserStatusEnum.SUSPENDED)
        calls.push(suspendUser(targetUser!.id, suspendIndefinite ? 0 : suspendDays, actionReason));
      else if (newStatus === UserStatusEnum.BANNED)
        calls.push(banUser(targetUser!.id, actionReason));
      else if (targetUser!.status === UserStatusEnum.SUSPENDED && newStatus === UserStatusEnum.ACTIVE)
        calls.push(unsuspendUser(targetUser!.id));
      else if (targetUser!.status === UserStatusEnum.BANNED && newStatus === UserStatusEnum.ACTIVE)
        calls.push(unbanUser(targetUser!.id));
      else
        calls.push(changeUserStatus(targetUser!.id, newStatus));
    }

    if (changePasswordValue)
      calls.push(changeUserPassword(targetUser!.id, changePasswordValue));

    if (calls.length === 0) {
      showToast(t("admin_no_changes"), "error");
      return;
    }

    await Promise.all(calls);
    showToast(t("admin_user_updated"), "success");
    closeActionModal();
    fetchUsers();
  };

  const handleSingleUserAction = async () => {
    switch (actionType) {
      case "changeRole":
        await changeUserRole(targetUser!.id, newRole);
        showToast(t("admin_user_role_changed"), "success");
        break;
      case "changeStatus":
        await changeUserStatus(targetUser!.id, newStatus);
        showToast(t("admin_user_status_changed"), "success");
        break;
      case "suspend":
        await suspendUser(targetUser!.id, suspendIndefinite ? 0 : suspendDays, actionReason);
        showToast(t("admin_user_suspended"), "success");
        break;
      case "unsuspend":
        await unsuspendUser(targetUser!.id);
        showToast(t("admin_user_unsuspended"), "success");
        break;
      case "ban":
        await banUser(targetUser!.id, actionReason);
        showToast(t("admin_user_banned"), "success");
        break;
      case "unban":
        await unbanUser(targetUser!.id);
        showToast(t("admin_user_unbanned"), "success");
        break;
      case "delete": {
        const isHard = deleteMode === "hard";
        await deleteUser(targetUser!.id, deleteEntries, hardDelete, isHard, deleteReason);
        showToast(t(isHard ? "admin_user_hard_deleted" : "admin_user_deleted"), "success");
        break;
      }
    }
    closeActionModal();
    fetchUsers();
  };

  const handleBulkAction = async () => {
    const userIds = Array.from(selectedUsers);
    switch (actionType) {
      case "bulkSuspend":
        await bulkSuspendUsers(userIds, suspendIndefinite ? 0 : suspendDays, actionReason);
        showToast(t("admin_users_bulk_suspended"), "success");
        break;
      case "bulkBan":
        await bulkBanUsers(userIds);
        showToast(t("admin_users_bulk_banned"), "success");
        break;
      case "bulkChangeRole":
        await bulkChangeRole(userIds, newRole);
        showToast(t("admin_users_bulk_role_changed"), "success");
        break;
      case "bulkDelete":
        await bulkDeleteUsers(userIds, deleteEntries, hardDelete, deleteMode === "hard", deleteReason);
        showToast(t("admin_users_bulk_deleted"), "success");
        break;
    }
    setSelectedUsers(new Set());
    setSelectAll(false);
    closeActionModal();
    fetchUsers();
  };

  const executeAction = async () => {
    try {
      setLoading(true);
      if (actionType === "createUser") return await handleCreateUser();
      if (actionType === "changePassword" && targetUser) return await handleChangePassword();
      if (actionType === "editUser" && targetUser) return await handleEditUser();
      if (targetUser) return await handleSingleUserAction();
      return await handleBulkAction();
    } catch (err: any) {
      // Through parseApiError rather than err.message: the backend answers with an
      // error CODE, which would otherwise be shown to the admin verbatim.
      // Fallback key is the admin-panel wording: an unmapped code used to come out
      // as "an error occurred during login", which is what a rejected moderator saw.
      showToast(parseApiError(err?.message ?? "", t, "admin_action_failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const viewProfile = (user: AdminUserDto) => {
    navigate(generateProfileUrl(user.username, user.id));
  };

  /**
   * Place the floating status tooltip against the hovered badge.
   * Opens above the badge by default and flips below it when the badge sits too
   * close to the top of the viewport (the first table rows), so the text is never
   * pushed off-screen.
   */
  const showStatusTooltip = (
    event: React.MouseEvent<HTMLElement>,
    text: string
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Rough height of a two-line tooltip plus its arrow — enough to decide the side
    const TOOLTIP_CLEARANCE = 90;
    const below = rect.top < TOOLTIP_CLEARANCE;
    setStatusTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: below ? rect.bottom + 6 : rect.top - 6,
      below,
    });
  };

  const hideStatusTooltip = () => setStatusTooltip(null);

  /**
   * Status badge of a single user. Extracted out of the table cell because the
   * mobile card list below renders the exact same badge — duplicating the
   * tooltip/label logic in two places would have guaranteed them drifting apart.
   */
  const renderStatusBadge = (user: AdminUserDto) => {
    if (user.status === UserStatusEnum.ACTIVE) {
      return <span className="status-active">{t("status_active")}</span>;
    }
    // Build tooltip text for statuses that have relevant lifecycle dates
    const dateInfo = getStatusDateInfo(user, t);
    // SUSPENDED uses suspensionEndDate instead of getStatusDateInfo.
    // The remaining time is spelled out next to the end date because
    // the suspension really does lift itself now (AuthService on
    // login, SuspensionExpiryScheduler hourly) — the date alone
    // makes the moderator do the subtraction.
    const suspensionRemaining = getSuspensionRemainingText(user.suspensionEndDate, t);
    // A SUSPENDED row with no end date is an open-ended suspension:
    // it never expires on its own, so say so instead of leaving the
    // tooltip silent about the term.
    const suspendedTooltip =
      user.status === UserStatusEnum.SUSPENDED
        ? (user.suspensionEndDate
            ? [
                `${t("admin_until")} ${formatAdminDate(user.suspensionEndDate)}`,
                suspensionRemaining,
              ].filter(Boolean).join("\n")
            : t("admin_suspend_indefinite_badge"))
        : null;
    const dateTooltip = suspendedTooltip
      ?? (dateInfo
        ? [dateInfo.primary, dateInfo.secondary].filter(Boolean).join("\n")
        : null);

    // A finished self-deletion still carries the DELETED_BY_USER
    // status, so the plain label would keep saying "in progress"
    // forever. closedAt is what marks it as done.
    const statusLabelKey =
      user.status === UserStatusEnum.DELETED_BY_USER && user.closedAt
        ? "status_closed_by_user"
        : `status_${user.status.toLowerCase()}`;

    // The badge itself is abbreviated to keep the status column
    // narrow; the tooltip always opens with the full wording and
    // only then adds the lifecycle dates, so hovering still gives
    // the complete picture.
    const tooltipText = [t(statusLabelKey), dateTooltip]
      .filter(Boolean)
      .join("\n");

    // The tooltip lives outside the scrolling table container,
    // so hovering the badge only reports where to draw it.
    return (
      <span
        className="status-badge-tooltip-wrapper"
        onMouseEnter={(e) => showStatusTooltip(e, tooltipText)}
        onMouseLeave={hideStatusTooltip}
      >
        <span className={`status-badge ${getStatusColor(user.status)}`}>
          {t(getStatusShortLabelKey(statusLabelKey))}
        </span>
      </span>
    );
  };

  const renderUserActions = (user: AdminUserDto) => {
    // A permanently closed account cannot be brought back: its profile, media,
    // messages and password are physically gone, and login is refused on closed_at
    // whatever the status column says. The backend rejects these calls, so the
    // buttons that would only produce that rejection are disabled here instead of
    // inviting the admin to try.
    const closed = !!user.closedAt;
    const closedTitle = t("admin_action_disabled_account_closed");

    // A moderator may not act on an ADMIN or MODERATOR account. The backend has
    // always refused these calls; the buttons are now greyed out so the refusal
    // is visible before the click instead of arriving as an error toast after it.
    const staffLocked = !canManageUser(user);
    const staffTitle = t("admin_action_disabled_staff_target");
    // Reason shown on a disabled button — closure is the stronger of the two
    const blockTitle = closed ? closedTitle : staffTitle;
    const blocked = closed || staffLocked;

    // Two further rules the backend applies to EVERY caller, admins included.
    // They used to be invisible in the panel: the button opened, the modal was
    // filled in, and only the confirm click produced the refusal.
    //   self      → role, status, suspend, ban and delete are refused
    //   adminRow  → status, suspend, ban and delete are refused (demote first)
    // Role change is deliberately NOT locked on another admin's row: demoting is
    // exactly how an admin account is made actionable.
    const self = isSelf(user);
    const adminRow = isAdminRow(user);
    const selfTitle = t("admin_action_disabled_self_target");
    const adminRowTitle = t("admin_action_disabled_admin_target");

    /**
     * Resolve why a button is disabled, strongest reason first. Passing the two
     * flags says which of the extra rules apply to that particular button, so
     * each one states the rule that actually stopped it rather than a generic
     * "not allowed".
     */
    const lockOf = (bySelf: boolean, byAdminRow: boolean): string | undefined => {
      if (closed) return closedTitle;
      if (staffLocked) return staffTitle;
      if (bySelf && self) return selfTitle;
      if (byAdminRow && adminRow) return adminRowTitle;
      return undefined;
    };

    // Role: blocked on the own row only
    const roleLock = lockOf(true, false);
    // Status / suspend / ban: blocked on the own row and on any admin row
    const statusLock = lockOf(true, true);
    // Delete is not affected by closure — a closed account may still be removed
    const deleteLock = self ? selfTitle : (adminRow ? adminRowTitle : undefined);

    // Edit modal: a protected target normally locks it, but a moderator may still
    // set the verified badge on another MODERATOR, and that field lives inside
    // this modal. So the button stays live there and only the other fields grey
    // out. On an ADMIN row a moderator has nothing left to change, so it locks.
    const verifiedOnly = staffLocked && canSetVerified(user);
    const editLock = closed ? closedTitle : (staffLocked && !verifiedOnly ? staffTitle : undefined);

    return (
      <div className="user-actions">
        <button className="action-btn view" onClick={() => viewProfile(user)} title={t("admin_action_view_profile")}><FaEye /></button>
        {/* Editing identity (username / e-mail / verified / password) and changing
            the role are meaningless on a closed account: the profile, media and
            messages are physically gone, login is refused on closed_at, and the
            role-change notification would be delivered to a mailbox that no longer
            exists. Deleting the row stays allowed — see ensureNotClosed() in
            AdminUserService. */}
        {/* Disabled on a protected target like every other write action, with ONE
            exception: the verified badge is still settable on a peer-level account,
            so a moderator keeps this button on another MODERATOR row. Everything
            else inside the modal is greyed out there — see canSetVerified(). */}
        <button className="action-btn edit-all" onClick={() => openActionModal("editUser", user)} title={editLock ?? (verifiedOnly ? t("admin_action_edit_user_verified_only") : t("admin_action_edit_user"))} disabled={!!editLock}><FaEdit /></button>
        <button className="action-btn edit" onClick={() => openActionModal("changeRole", user)} title={roleLock ?? t("admin_action_change_role")} disabled={!!roleLock}><FaUserEdit /></button>
        {/* Durum değiştirme yalnızca ADMIN: changeUserStatus backend'de requireAdmin.
            Moderatöre gösterilmesi tek sonucu 403 olan bir buton demekti. */}
        {isAdmin && (
          <button className="action-btn warning" onClick={() => openActionModal("changeStatus", user)} title={statusLock ?? t("admin_action_change_status")} disabled={!!statusLock}><FaToggleOn /></button>
        )}
        <button className="action-btn key" onClick={() => openActionModal("changePassword", user)} title={blocked ? blockTitle : t("admin_action_change_password")} disabled={blocked}><FaKey /></button>
        {/* Lifting a suspension is deliberately left open on an admin row: the
            backend allows it (unsuspendUser has no admin-target rule) so an
            already suspended admin never needs a demotion just to be restored. */}
        {user.status === UserStatusEnum.SUSPENDED ? (
          <button className="action-btn success" onClick={() => openActionModal("unsuspend", user)} title={blocked ? blockTitle : t("admin_action_unsuspend")} disabled={blocked}><FaPlayCircle /></button>
        ) : (
          <button className="action-btn warning" onClick={() => openActionModal("suspend", user)} title={statusLock ?? t("admin_action_suspend")} disabled={!!statusLock}><FaPauseCircle /></button>
        )}
        {isAdmin && (
          <>
            {/* Same reasoning as unsuspend: unbanUser carries no admin-target rule,
                so restoring a banned admin row stays possible. */}
            {user.status === UserStatusEnum.BANNED ? (
              <button className="action-btn success" onClick={() => openActionModal("unban", user)} title={closed ? closedTitle : t("admin_action_unban")} disabled={closed}><FaUndo /></button>
            ) : (
              <button className="action-btn danger" onClick={() => openActionModal("ban", user)} title={statusLock ?? t("admin_action_ban")} disabled={!!statusLock}><FaBan /></button>
            )}
            <button className="action-btn danger" onClick={() => openActionModal("delete", user)} title={deleteLock ?? t("admin_action_delete")} disabled={!!deleteLock}><FaTrash /></button>
          </>
        )}
      </div>
    );
  };

  const renderBulkActions = () => {
    if (selectedUsers.size === 0) return null;
    return (
      <div className="bulk-actions-bar">
        <span className="selected-count">{t("admin_selected_count", { count: selectedUsers.size })}</span>
        <div className="bulk-buttons">
          <button className="bulk-btn warning" onClick={() => openActionModal("bulkSuspend")}><FaPauseCircle /> {t("admin_bulk_suspend")}</button>
          {isAdmin && (
            <>
              <button className="bulk-btn danger" onClick={() => openActionModal("bulkBan")}><FaBan /> {t("admin_bulk_ban")}</button>
              <button className="bulk-btn edit" onClick={() => openActionModal("bulkChangeRole")}><FaUserEdit /> {t("admin_bulk_change_role")}</button>
              <button className="bulk-btn danger" onClick={() => openActionModal("bulkDelete")}><FaTrash /> {t("admin_bulk_delete")}</button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderModalContent = () => {
    const isBulk = !targetUser && actionType.startsWith("bulk");
    let title = "";
    if (actionType === "createUser") { title = t("admin_modal_create_user_title"); }
    else if (actionType === "editUser" && targetUser) { title = t("admin_modal_editUser_title_single", { username: targetUser.username }); }
    else if (actionType === "changePassword" && targetUser) { title = t("admin_modal_change_password_title", { username: targetUser.username }); }
    else if (isBulk) { title = t(`admin_modal_${actionType}_title`); }
    else if (targetUser) { title = t(`admin_modal_${actionType}_title_single`, { username: targetUser.username }); }

    return (
      <div className="action-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeActionModal(); }}>
        <div className={`action-modal${actionType === "editUser" ? " edit-user-modal" : ""}`}>
          <div className="modal-header">
            <h3>{title}</h3>
            <button className="modal-close" onClick={closeActionModal}><FaTimes /></button>
          </div>
          <div className="modal-body">
            {actionType === "createUser" && (
              <>
                <div className="form-group">
                  <label>{t("admin_username")}</label>
                  <input type="text" value={newUsername} maxLength={usernameMaxLength} onChange={(e) => setNewUsername(e.target.value.toLocaleLowerCase("tr"))} placeholder={t("admin_username_placeholder")} />
                  <AdminCharCounter value={newUsername} max={usernameMaxLength} />
                </div>
                <div className="form-group">
                  <label>{t("admin_email")}</label>
                  <input type="email" value={newEmail} maxLength={ADMIN_EMAIL_MAX_LENGTH} onChange={(e) => setNewEmail(e.target.value)} placeholder={t("admin_email_placeholder")} />
                  <AdminCharCounter value={newEmail} max={ADMIN_EMAIL_MAX_LENGTH} />
                </div>
                <div className="form-group">
                  <label>{t("admin_password")}</label>
                  <input type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t("admin_password_placeholder", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })} />
                  {/* Rules are stated up-front, not only after a failed submit.
                      NOTE: no maxLength attribute on purpose — the browser would
                      silently truncate to the limit, making the request valid and
                      hiding the reason from the admin. See RegisterModal.tsx.
                      Hint and counter share one line so the field lines up with
                      the counter-only fields next to it. */}
                  <div className="admin-field-footer">
                    <span className="form-hint">{t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}</span>
                    <AdminCharCounter value={newPassword} max={PASSWORD_MAX_LENGTH} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t("admin_new_role")}</label>
                  <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value as UserRoleEnum)}>
                    <option value={UserRoleEnum.ROOKIE}>{t("status_rookie")}</option>
                    <option value={UserRoleEnum.AUTHOR}>{t("status_author")}</option>
                    <option value={UserRoleEnum.MODERATOR}>{t("status_moderator")}</option>
                    <option value={UserRoleEnum.ADMIN}>{t("status_admin")}</option>
                  </select>
                </div>
              </>
            )}
            {actionType === "changePassword" && (
              <>
                <div className="form-group">
                  <label>{t("admin_new_password")}</label>
                  <input type="password" autoComplete="new-password" value={changePasswordValue} onChange={(e) => setChangePasswordValue(e.target.value)} placeholder={t("admin_new_password_placeholder")} />
                  {/* Rules are stated up-front, not only after a failed submit.
                      NOTE: no maxLength attribute on purpose — the browser would
                      silently truncate to the limit, making the request valid and
                      hiding the reason from the admin. See RegisterModal.tsx.
                      Hint and counter share one line so the field lines up with
                      the counter-only fields next to it. */}
                  <div className="admin-field-footer">
                    <span className="form-hint">{t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}</span>
                    <AdminCharCounter value={changePasswordValue} max={PASSWORD_MAX_LENGTH} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t("admin_confirm_password")}</label>
                  <input type="password" autoComplete="new-password" value={changePasswordConfirm} onChange={(e) => setChangePasswordConfirm(e.target.value)} placeholder={t("admin_confirm_password_placeholder")} />
                  <AdminCharCounter value={changePasswordConfirm} max={PASSWORD_MAX_LENGTH} />
                </div>
                {/* The confirm field is no longer required to be non-empty here.
                    A browser-autofilled password with an empty confirm field used
                    to leave the confirm button disabled with nothing on screen
                    explaining why. */}
                {changePasswordValue && changePasswordValue !== changePasswordConfirm && (
                  <p className="error-text">{t("admin_password_mismatch")}</p>
                )}
              </>
            )}
            {actionType === "editUser" && targetUser && (
              <>
                {/* Read-only info row */}
                <div className="edit-user-info-row">
                  <span className="edit-user-info-label">@{targetUser.username}</span>
                  <span className="edit-user-info-email">{targetUser.email}</span>
                </div>

                {/* ── Section: Identity (ADMIN only) ── */}
                {isAdmin && (
                  <>
                    <div className="edit-section-divider">{t("admin_edit_section_identity")}</div>
                    <div className="form-group">
                      <label>{t("admin_username")}</label>
                      <input
                        type="text"
                        value={editNewUsername}
                        maxLength={usernameMaxLength}
                        onChange={(e) => setEditNewUsername(e.target.value.toLocaleLowerCase("tr"))}
                        placeholder={t("admin_username_placeholder")}
                      />
                      <AdminCharCounter value={editNewUsername} max={usernameMaxLength} />
                    </div>
                    <div className="form-group">
                      <label>{t("admin_email")}</label>
                      <input
                        type="email"
                        value={editNewEmail}
                        maxLength={ADMIN_EMAIL_MAX_LENGTH}
                        onChange={(e) => setEditNewEmail(e.target.value)}
                        placeholder={t("admin_email_placeholder")}
                      />
                      <AdminCharCounter value={editNewEmail} max={ADMIN_EMAIL_MAX_LENGTH} />
                    </div>
                  </>
                )}

                {/* ── Section: Verified badge ── */}
                <div className="edit-section-divider">{t("admin_edit_section_verified")}</div>
                <div className={`form-group edit-verified-row${verifiedFieldLocked ? " field-locked" : ""}`}>
                  <label className="edit-verified-label">
                    <input
                      type="checkbox"
                      checked={editVerified}
                      onChange={(e) => setEditVerified(e.target.checked)}
                      disabled={verifiedFieldLocked}
                    />
                    <span>{t("admin_edit_verified_label")}</span>
                  </label>
                  <span className="edit-verified-hint">{t("admin_edit_verified_hint")}</span>
                  {/* On a protected target this is the only live field in the whole
                      modal — say so, otherwise the rest looking grey reads as a bug */}
                  {targetLocked && !verifiedFieldLocked && (
                    <span className="edit-verified-hint">{t("admin_edit_verified_only_editable")}</span>
                  )}
                </div>

                {/* ── Section: Role ── */}
                <div className="edit-section-divider">{t("admin_edit_section_role")}</div>
                <div className={`form-group${roleFieldLocked ? " field-locked" : ""}`}>
                  <label>{t("admin_new_role")}</label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRoleEnum)}
                    disabled={roleFieldLocked}
                  >
                    <option value={UserRoleEnum.ROOKIE}>{t("status_rookie")}</option>
                    <option value={UserRoleEnum.AUTHOR}>{t("status_author")}</option>
                    {isAdmin && (
                      <>
                        <option value={UserRoleEnum.MODERATOR}>{t("status_moderator")}</option>
                        <option value={UserRoleEnum.ADMIN}>{t("status_admin")}</option>
                      </>
                    )}
                  </select>
                  {/* Say WHY the field is grey — a disabled select with no
                      explanation reads like a bug */}
                  {targetIsSelf && !targetLocked && (
                    <span className="form-hint">{t("admin_edit_role_locked_self")}</span>
                  )}
                </div>

                {/* ── Section: Status ── */}
                <div className="edit-section-divider">{t("admin_edit_section_status")}</div>
                <div className={`form-group${statusFieldLocked ? " field-locked" : ""}`}>
                  <label>{t("admin_new_status")}</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as UserStatusEnum)}
                    disabled={statusFieldLocked}
                  >
                    <option value={UserStatusEnum.ACTIVE}>{t("status_active")}</option>
                    <option value={UserStatusEnum.SUSPENDED}>{t("status_suspended")}</option>
                    {/* Askıya alma / askıyı kaldırma dışındaki her hedef durum
                        changeUserStatus, banUser veya unbanUser'a gidiyor; üçü de
                        backend'de ADMIN istiyor. Moderatöre gösterip 403 aldırmak
                        yerine listeden çıkarıyoruz. */}
                    {isAdmin && (
                      <>
                        <option value={UserStatusEnum.BANNED}>{t("status_banned")}</option>
                        <option value={UserStatusEnum.INACTIVE}>{t("status_inactive")}</option>
                        <option value={UserStatusEnum.FROZEN_BY_USER}>{t("status_frozen_by_user")}</option>
                        <option value={UserStatusEnum.DELETED_BY_USER}>{t("status_deleted_by_user")}</option>
                        <option value={UserStatusEnum.DELETED_BY_ADMIN}>{t("status_deleted_by_admin")}</option>
                      </>
                    )}
                  </select>
                  {/* Same as the role field above: name the rule that greyed it out */}
                  {!targetLocked && (targetIsSelf || targetIsAdminRow) && (
                    <span className="form-hint">
                      {targetIsSelf
                        ? t("admin_edit_status_locked_self")
                        : t("admin_edit_status_locked_admin")}
                    </span>
                  )}
                </div>
                {/* Suspension-specific fields shown only when SUSPENDED is selected */}
                {newStatus === UserStatusEnum.SUSPENDED && (
                  <>
                    <div className="form-group">
                      <label>{t("admin_suspend_days")}</label>
                      <NumberStepper
                        value={suspendDays}
                        min={1}
                        max={SUSPENSION_MAX_DAYS}
                        emptyValue={SUSPENSION_DEFAULT_DAYS}
                        disabled={suspendIndefinite}
                        onChange={(next) => setSuspendDays(next ?? SUSPENSION_DEFAULT_DAYS)}
                      />
                      {/* Open-ended suspension — disables the day stepper and sends 0 */}
                      <label className="admin-inline-checkbox">
                        <input
                          type="checkbox"
                          checked={suspendIndefinite}
                          onChange={(e) => setSuspendIndefinite(e.target.checked)}
                        />
                        <span>{t("admin_suspend_indefinite")}</span>
                      </label>
                      <span className="form-hint">{t("admin_suspend_indefinite_hint")}</span>
                    </div>
                    <div className="form-group">
                      <label>{t("admin_reason")}</label>
                      <textarea
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        placeholder={t("admin_reason_placeholder")}
                        maxLength={ADMIN_REASON_MAX_LENGTH}
                      />
                      <AdminCharCounter value={actionReason} max={ADMIN_REASON_MAX_LENGTH} />
                    </div>
                  </>
                )}
                {/* Ban reason shown when BANNED is selected */}
                {newStatus === UserStatusEnum.BANNED && (
                  <div className="form-group">
                    <label>{t("admin_reason")}</label>
                    <textarea
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      placeholder={t("admin_reason_placeholder")}
                      maxLength={ADMIN_REASON_MAX_LENGTH}
                    />
                    <AdminCharCounter value={actionReason} max={ADMIN_REASON_MAX_LENGTH} />
                  </div>
                )}

                {/* ── Section: Password (optional) ── */}
                <div className="edit-section-divider">{t("admin_edit_section_password")}</div>
                <div className={`form-group${targetLocked ? " field-locked" : ""}`}>
                  <label>{t("admin_new_password")}</label>
                  <input
                    type="password"
                    /* autoComplete="new-password" keeps Chrome from filling this
                       field with the signed-in admin's own saved credentials.
                       The modal has username and e-mail inputs directly above it,
                       which is exactly the shape Chrome treats as a login form.
                       An autofilled password with an empty confirm field made the
                       confirm button inert, so unrelated edits in the same modal
                       (role, status, verified badge) were silently never sent. */
                    autoComplete="new-password"
                    value={changePasswordValue}
                    onChange={(e) => setChangePasswordValue(e.target.value)}
                    placeholder={t("admin_new_password_placeholder")}
                    disabled={targetLocked}
                  />
                  {/* Rules are stated up-front, not only after a failed submit.
                      NOTE: no maxLength attribute on purpose — the browser would
                      silently truncate to the limit, making the request valid and
                      hiding the reason from the admin. See RegisterModal.tsx.
                      Hint and counter share one line so the field lines up with
                      the counter-only fields next to it. */}
                  <div className="admin-field-footer">
                    <span className="form-hint">{t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}</span>
                    <AdminCharCounter value={changePasswordValue} max={PASSWORD_MAX_LENGTH} />
                  </div>
                </div>
                {changePasswordValue && (
                  <div className="form-group">
                    <label>{t("admin_confirm_password")}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={changePasswordConfirm}
                      onChange={(e) => setChangePasswordConfirm(e.target.value)}
                      placeholder={t("admin_confirm_password_placeholder")}
                      />
                    <AdminCharCounter value={changePasswordConfirm} max={PASSWORD_MAX_LENGTH} />
                    {/* Shown while the confirm field is still empty as well: the
                        confirm button is already disabled at that point, and an
                        unexplained disabled button is what hid this bug. */}
                    {changePasswordValue !== changePasswordConfirm && (
                      <p className="error-text">{t("admin_password_mismatch")}</p>
                    )}
                  </div>
                )}
              </>
            )}
            {(actionType === "suspend" || actionType === "bulkSuspend") && (
              <>
                <div className="form-group">
                  <label>{t("admin_suspend_days")}</label>
                  <NumberStepper
                    value={suspendDays}
                    min={1}
                    max={SUSPENSION_MAX_DAYS}
                    emptyValue={SUSPENSION_DEFAULT_DAYS}
                    disabled={suspendIndefinite}
                    onChange={(next) => setSuspendDays(next ?? SUSPENSION_DEFAULT_DAYS)}
                  />
                  {/* Open-ended suspension — disables the day stepper and sends 0 */}
                  <label className="admin-inline-checkbox">
                    <input
                      type="checkbox"
                      checked={suspendIndefinite}
                      onChange={(e) => setSuspendIndefinite(e.target.checked)}
                    />
                    <span>{t("admin_suspend_indefinite")}</span>
                  </label>
                  <span className="form-hint">{t("admin_suspend_indefinite_hint")}</span>
                </div>
                <div className="form-group">
                  <label>{t("admin_reason")}</label>
                  <textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder={t("admin_reason_placeholder")} maxLength={ADMIN_REASON_MAX_LENGTH} />
                  <AdminCharCounter value={actionReason} max={ADMIN_REASON_MAX_LENGTH} />
                </div>
              </>
            )}
            {actionType === "ban" && (
              <div className="form-group">
                <label>{t("admin_reason")}</label>
                <textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder={t("admin_reason_placeholder")} maxLength={ADMIN_REASON_MAX_LENGTH} />
                <AdminCharCounter value={actionReason} max={ADMIN_REASON_MAX_LENGTH} />
              </div>
            )}
            {(actionType === "changeRole" || actionType === "bulkChangeRole") && (
              <div className="form-group">
                <label>{t("admin_new_role")}</label>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value as UserRoleEnum)}>
                  <option value={UserRoleEnum.ROOKIE}>{t("status_rookie")}</option>
                  <option value={UserRoleEnum.AUTHOR}>{t("status_author")}</option>
                  {isAdmin && (
                    <>
                      <option value={UserRoleEnum.MODERATOR}>{t("status_moderator")}</option>
                      <option value={UserRoleEnum.ADMIN}>{t("status_admin")}</option>
                    </>
                  )}
                                  </select>
              </div>
            )}
            {actionType === "changeStatus" && targetUser && (
              <div className="form-group">
                <label>{t("admin_new_status")}</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as UserStatusEnum)}>
                  <option value={UserStatusEnum.ACTIVE}>{t("status_active")}</option>
                  <option value={UserStatusEnum.SUSPENDED}>{t("status_suspended")}</option>
                  <option value={UserStatusEnum.BANNED}>{t("status_banned")}</option>
                  <option value={UserStatusEnum.INACTIVE}>{t("status_inactive")}</option>
                  <option value={UserStatusEnum.FROZEN_BY_USER}>{t("status_frozen_by_user")}</option>
                  <option value={UserStatusEnum.DELETED_BY_USER}>{t("status_deleted_by_user")}</option>
                  <option value={UserStatusEnum.DELETED_BY_ADMIN}>{t("status_deleted_by_admin")}</option>
                </select>
              </div>
            )}
            {(actionType === "delete" || actionType === "bulkDelete") && (
              <div className="delete-mode-selector">
                {/* ── Section 1: SOFT DELETE ── */}
                <label className={`delete-mode-card ${deleteMode === "soft" ? "selected" : ""}`}>
                  <div className="delete-mode-card-header">
                    <input
                      type="radio"
                      name="deleteMode"
                      checked={deleteMode === "soft"}
                      onChange={() => { setDeleteMode("soft"); setHardDeleteConfirmText(""); }}
                    />
                    <span className="delete-mode-title">{t("admin_delete_mode_soft_title")}</span>
                  </div>
                  <p className="delete-mode-desc">{t("admin_delete_mode_soft_desc")}</p>
                  {/* What exactly happens on soft delete — quick reference list */}
                  <ul className="delete-mode-facts">
                    <li>{t("admin_delete_fact_soft_db")}</li>
                    <li>{t("admin_delete_fact_soft_login")}</li>
                    <li>{t("admin_delete_fact_soft_search")}</li>
                    <li>{t("admin_delete_fact_soft_profile")}</li>
                    <li>{t("admin_delete_fact_soft_nickalti")}</li>
                    <li>{t("admin_delete_fact_soft_reregister")}</li>
                    <li>{t("admin_delete_fact_soft_reactivate")}</li>
                  </ul>
                  {deleteMode === "soft" && (
                    <div className="delete-mode-options">
                      <label className="delete-entry-checkbox-label">
                        <input
                          type="checkbox"
                          checked={deleteEntries}
                          onChange={e => { setDeleteEntries(e.target.checked); if (!e.target.checked) setHardDelete(false); }}
                        />
                        {t("admin_delete_user_entries")}
                      </label>
                      {deleteEntries && (
                        <>
                          <label className="delete-entry-checkbox-label danger">
                            <input
                              type="checkbox"
                              checked={hardDelete}
                              onChange={e => setHardDelete(e.target.checked)}
                            />
                            {t("admin_delete_user_entries_hard")}
                          </label>
                          {hardDelete && <p className="warning-text">{t("admin_delete_user_entries_hard_warning")}</p>}
                        </>
                      )}
                    </div>
                  )}
                </label>

                {/* ── Section 2: HARD DELETE ── */}
                <label className={`delete-mode-card danger ${deleteMode === "hard" ? "selected" : ""}`}>
                  <div className="delete-mode-card-header">
                    <input
                      type="radio"
                      name="deleteMode"
                      checked={deleteMode === "hard"}
                      onChange={() => { setDeleteMode("hard"); setHardDeleteConfirmText(""); }}
                    />
                    <span className="delete-mode-title danger">{t("admin_delete_mode_hard_title")}</span>
                  </div>
                  <p className="delete-mode-desc">{t("admin_delete_mode_hard_desc")}</p>
                  {/* What exactly happens on hard delete — quick reference list */}
                  <ul className="delete-mode-facts">
                    <li>{t("admin_delete_fact_hard_db")}</li>
                    <li>{t("admin_delete_fact_hard_login")}</li>
                    <li>{t("admin_delete_fact_hard_search")}</li>
                    <li>{t("admin_delete_fact_hard_profile")}</li>
                    <li>{t("admin_delete_fact_hard_nickalti")}</li>
                    <li>{t("admin_delete_fact_hard_reregister")}</li>
                  </ul>
                  {deleteMode === "hard" && (
                    <div className="delete-mode-options">
                      <p className="warning-text">{t("admin_delete_user_hard_warning")}</p>
                      <div className="form-group">
                        <label>
                          {t("admin_delete_user_hard_confirm_label", {
                            // Single delete: type the exact username. Bulk delete: type the confirm word.
                            word: actionType === "delete" && targetUser
                              ? targetUser.username
                              : t("admin_delete_user_hard_confirm_word"),
                          })}
                        </label>
                        <input
                          type="text"
                          value={hardDeleteConfirmText}
                          maxLength={usernameMaxLength}
                          onChange={e => setHardDeleteConfirmText(e.target.value)}
                          autoComplete="off"
                        />
                        <AdminCharCounter value={hardDeleteConfirmText} max={usernameMaxLength} />
                      </div>
                    </div>
                  )}
                </label>

                {/* Optional admin note — recorded in the audit log for both modes */}
                <div className="form-group delete-reason-group">
                  <label>{t("admin_delete_reason_label")}</label>
                  <textarea
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    placeholder={t("admin_delete_reason_placeholder")}
                    rows={2}
                    maxLength={ADMIN_REASON_MAX_LENGTH}
                  />
                  <AdminCharCounter value={deleteReason} max={ADMIN_REASON_MAX_LENGTH} />
                </div>
              </div>
            )}
            {(actionType === "unsuspend" || actionType === "unban") && <p>{t(`admin_confirm_${actionType}`)}</p>}
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={closeActionModal}>{t("admin_cancel")}</button>
            <button
              className={`btn-confirm ${actionType.includes("delete") || actionType.includes("ban") ? "danger" : ""}`}
              onClick={executeAction}
              disabled={
                // Nothing on a locked target is editable, so confirming could only
                // report "no changes" — with one exception: the verified badge in
                // the edit modal, which a moderator may still set on a peer-level
                // MODERATOR account.
                (targetLocked && !(actionType === "editUser" && !verifiedFieldLocked)) ||
                // Safety net for the single-purpose modals. Their buttons are
                // already disabled on these targets, so this should never fire —
                // it keeps confirm inert if some future path opens one of them
                // without going through the row buttons.
                (actionType === "changeRole" && targetIsSelf) ||
                (["changeStatus", "suspend", "ban", "delete"].includes(actionType) &&
                  (targetIsSelf || targetIsAdminRow)) ||
                (actionType === "changePassword" && (changePasswordValue !== changePasswordConfirm || !!validatePasswordInput(changePasswordValue))) ||
                (actionType === "editUser" && !!changePasswordValue && (changePasswordValue !== changePasswordConfirm || !!validatePasswordInput(changePasswordValue))) ||
                // Hard delete requires the typed confirmation to match exactly:
                // the username for a single delete, the confirm word for bulk delete.
                // Soft delete needs no confirmation text.
                ((actionType === "delete" || actionType === "bulkDelete") && deleteMode === "hard" &&
                  hardDeleteConfirmText !== (actionType === "delete" && targetUser
                    ? targetUser.username
                    : t("admin_delete_user_hard_confirm_word")))
              }
            >
              <FaCheck /> {t("admin_confirm")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-users">
      {successToast && <div className="toast success">{successToast}</div>}
      {errorToast && <div className="toast error">{errorToast}</div>}

      <div className="users-toolbar">
        <div className="search-box">
          <input type="text" placeholder={t("admin_search_placeholder")} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyPress={(e) => e.key === "Enter" && handleSearch()} />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}><FaSearch /></button>
        </div>
        <button className={`filter-toggle ${showFilters ? "active" : ""}`} onClick={() => setShowFilters(!showFilters)}>
          <FaFilter /> {t("admin_filters")}
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>
        {(filters.status || filters.role || filters.search) && (
          <button className="clear-filters" onClick={clearFilters}><FaTimes /> {t("admin_clear_filters")}</button>
        )}
        {/* Right-hand group — pushed to the far right by margin-left:auto so the
            create button keeps the position it had before the refresh control
            was added next to it. */}
        <div className="users-toolbar-right">
          {lastRefreshedAt && (
            <span className="users-last-refreshed">
              {t("admin_users_last_refreshed", { time: formatRefreshTime(lastRefreshedAt) })}
            </span>
          )}
          <AdminRefreshButton
            onClick={() => fetchUsers(true)}
            refreshing={refreshing}
            disabled={loading}
            label={t("admin_users_refresh")}
          />
          {isAdmin && (
            <button className="create-user-btn" onClick={() => openActionModal("createUser")}><FaUserPlus /> {t("admin_create_user")}</button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="admin-filter-panel">
          {/* Role filter — sends ?role= param (UserRole enum: ADMIN, MODERATOR, AUTHOR, ROOKIE) */}
          <div className="admin-filter-group">
            <label>{t("admin_filter_role")}</label>
            <select value={filters.role ?? ""} onChange={(e) => handleFilterChange("role", e.target.value)}>
              <option value="">{t("admin_all_roles")}</option>
              <option value={UserRoleEnum.ADMIN}>{t("status_admin")}</option>
              <option value={UserRoleEnum.MODERATOR}>{t("status_moderator")}</option>
              <option value={UserRoleEnum.AUTHOR}>{t("status_author")}</option>
              <option value={UserRoleEnum.ROOKIE}>{t("status_rookie")}</option>
            </select>
          </div>
          {/* Status filter — sends ?status= param (UserStatus enum: lifecycle states only) */}
          <div className="admin-filter-group">
            <label>{t("admin_filter_status")}</label>
            <select value={filters.status ?? ""} onChange={(e) => handleFilterChange("status", e.target.value)}>
              <option value="">{t("admin_all_statuses")}</option>
              <option value={UserStatusEnum.ACTIVE}>{t("status_active")}</option>
              <option value={UserStatusEnum.SUSPENDED}>{t("status_suspended")}</option>
              <option value={UserStatusEnum.BANNED}>{t("status_banned")}</option>
              <option value={UserStatusEnum.INACTIVE}>{t("status_inactive")}</option>
              <option value={UserStatusEnum.FROZEN_BY_USER}>{t("status_frozen_by_user")}</option>
              <option value={UserStatusEnum.DELETED_BY_USER}>{t("status_deleted_by_user")}</option>
              <option value={UserStatusEnum.DELETED_BY_ADMIN}>{t("status_deleted_by_admin")}</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_date_from")}</label>
            <input type="date" value={filters.startDate?.split("T")[0] || ""} onChange={(e) => handleFilterChange("startDate", e.target.value ? `${e.target.value}T00:00:00` : "")} />
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_date_to")}</label>
            <input type="date" value={filters.endDate?.split("T")[0] || ""} onChange={(e) => handleFilterChange("endDate", e.target.value ? `${e.target.value}T23:59:59` : "")} />
          </div>
        </div>
      )}

      {renderBulkActions()}

      <div className="users-table-container">
        {loading ? (
          <div className="loading-spinner">{t("loading")}</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : users.length === 0 ? (
          <div className="no-data">{t("admin_no_users_found")}</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th className="checkbox-col"><input type="checkbox" checked={selectAll} onChange={handleSelectAll} /></th>
                <th className="id-col" onClick={() => handleSort("id")}>ID {getSortIcon("id")}</th>
                <th className="username-col" onClick={() => handleSort("username")}>{t("admin_col_username")} {getSortIcon("username")}</th>
                <th className="email-col" onClick={() => handleSort("email")}>{t("admin_col_email")} {getSortIcon("email")}</th>
                <th className="role-col" onClick={() => handleSort("role")}>{t("admin_col_role")} {getSortIcon("role")}</th>
                <th className="status-col" onClick={() => handleSort("status")}>{t("admin_col_status")} {getSortIcon("status")}</th>
                <th className="karma-col" onClick={() => handleSort("karma")}>{t("admin_col_karma")} {getSortIcon("karma")}</th>
                <th className="entries-col" onClick={() => handleSort("entryCount")}>{t("admin_col_entries")} {getSortIcon("entryCount")}</th>
                <th className="date-col" onClick={() => handleSort("registrationDate")}>{t("admin_col_registration")} {getSortIcon("registrationDate")}</th>
                <th className="date-col" onClick={() => handleSort("lastLoginDate")}>{t("admin_col_last_login")} {getSortIcon("lastLoginDate")}</th>
                <th className="ip-col">{t("admin_col_ip")}</th>
                <th className="actions-col">{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={selectedUsers.has(user.id) ? "selected" : ""}>
                  {/* Rows this account cannot act on are not selectable — a bulk
                      call including them would only fail for those rows. */}
                  {/* Every body cell carries the same *-col class as its header
                      cell. The responsive rules hide columns by that class, and
                      the cells that were missing it (e-posta, karma, kayıt/son
                      giriş) stayed visible while their headers disappeared —
                      which is what shifted the whole header row out of line and
                      left the empty strip on the right below 1024px. */}
                  <td className="checkbox-col"><input
                    type="checkbox"
                    checked={selectedUsers.has(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    disabled={!isSelectable(user)}
                    title={
                      isSelectable(user)
                        ? undefined
                        : (isSelf(user)
                            ? t("admin_action_disabled_self_target")
                            : t("admin_action_disabled_staff_target"))
                    }
                  /></td>
                  <td className="id-col">{user.id}</td>
                  <td className="username-cell username-col" title={`@${user.username}`}><span className="username-link" onClick={() => viewProfile(user)}>@{user.username}</span></td>
                  {/* email-cell ellipsizes long addresses; full value on hover */}
                  <td className="email-cell email-col" title={user.email}>{user.email}</td>
                  <td className="role-col">
                    <span className={`status-badge ${getRoleColor(user.role)}`}>{t(`status_${user.role.toLowerCase()}`)}</span>
                  </td>
                  <td className="status-col">
                    {renderStatusBadge(user)}
                  </td>
                  <td className={`karma-col ${user.karma >= 0 ? "karma-positive" : "karma-negative"}`}>{user.karma}</td>
                  <td className="entries-col">{user.entryCount}</td>
                  <td className="date-col">{formatAdminDate(user.registrationDate)}</td>
                  <td className="date-col">{formatAdminDate(user.lastLoginDate)}</td>
                  <td className="ip-col">
                    <span className="ip-address-text">{user.ipAddress || "-"}</span>
                  </td>
                  <td className="actions-col">{renderUserActions(user)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Mobile card list ──────────────────────────────────────────────
          Twelve columns never fit a phone: squeezing them only produced a
          horizontally scrolling table whose header no longer lined up with its
          body. Below 640px the table above is hidden and the same users are
          rendered as real cards instead (see the CSS) — one card per user, the
          identifying fields on top, the rest as label/value rows, and the
          action buttons wrapping across the full card width.
          The data comes from the same `users` array and the same helpers as the
          table, so the two views can never show different information. */}
      {!loading && !error && users.length > 0 && (
        <div className="users-cards">
          {users.map((user) => (
            <div key={user.id} className={`user-card${selectedUsers.has(user.id) ? " selected" : ""}`}>
              <div className="user-card__head">
                <input
                  type="checkbox"
                  className="user-card__select"
                  checked={selectedUsers.has(user.id)}
                  onChange={() => handleSelectUser(user.id)}
                  disabled={!isSelectable(user)}
                  title={
                    isSelectable(user)
                      ? undefined
                      : (isSelf(user)
                          ? t("admin_action_disabled_self_target")
                          : t("admin_action_disabled_staff_target"))
                  }
                />
                <span className="user-card__username" onClick={() => viewProfile(user)}>
                  @{user.username}
                </span>
                <span className="user-card__id">#{user.id}</span>
              </div>

              <div className="user-card__badges">
                <span className={`status-badge ${getRoleColor(user.role)}`}>{t(`status_${user.role.toLowerCase()}`)}</span>
                {renderStatusBadge(user)}
              </div>

              <div className="user-card__fields">
                <div className="user-card__field user-card__field--wide">
                  <span className="user-card__label">{t("admin_col_email")}</span>
                  <span className="user-card__value user-card__value--break">{user.email}</span>
                </div>
                <div className="user-card__field">
                  <span className="user-card__label">{t("admin_col_karma")}</span>
                  <span className={`user-card__value ${user.karma >= 0 ? "karma-positive" : "karma-negative"}`}>{user.karma}</span>
                </div>
                <div className="user-card__field">
                  <span className="user-card__label">{t("admin_col_entries")}</span>
                  <span className="user-card__value">{user.entryCount}</span>
                </div>
                <div className="user-card__field">
                  <span className="user-card__label">{t("admin_col_registration")}</span>
                  <span className="user-card__value">{formatAdminDate(user.registrationDate)}</span>
                </div>
                <div className="user-card__field">
                  <span className="user-card__label">{t("admin_col_last_login")}</span>
                  <span className="user-card__value">{formatAdminDate(user.lastLoginDate)}</span>
                </div>
                <div className="user-card__field user-card__field--wide">
                  <span className="user-card__label">{t("admin_col_ip")}</span>
                  <span className="user-card__value ip-address-text">{user.ipAddress || "-"}</span>
                </div>
              </div>

              <div className="user-card__actions">{renderUserActions(user)}</div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="users-pagination">
          <span className="page-info">{t("admin_page_info", { current: currentPage + 1, total: totalPages, items: totalItems })}</span>
          {/* Shared Pagination component — same control on every list in the app.
              It is 1-indexed while this screen keeps 0-indexed state, hence the +/- 1. */}
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={(page) => setCurrentPage(page - 1)}
          />
          <select className="page-size-select" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }}>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}

      {showActionModal && renderModalContent()}

      {/* Rendered at the component root, outside .users-table-container, so the
          scroll container cannot clip it. Coordinates come from showStatusTooltip. */}
      {statusTooltip && (
        <div
          className={`status-badge-tooltip${statusTooltip.below ? " below" : ""}`}
          style={{ left: statusTooltip.x, top: statusTooltip.y }}
        >
          {statusTooltip.text}
        </div>
      )}
    </div>
  );
};